import { call, put, select } from 'redux-saga/effects';
import {
  LoadFileListAction,
  setDetailsFolderList,
  setFileList,
  setFolderList,
  setState,
} from '../actions';
import { getCompanyLibrary } from '../selectors';
import api from '../../../../../../services/api';
import { message, TablePaginationConfig } from 'antd';
import { CompanyLibraryState } from '../reducers';
import { updateRelations } from './uploadFileWorker';

export function* loadFileListWorker(action: LoadFileListAction) {
  const { isInitialRender, clientContractOpts } = action.payload;
  try {
    const {
      pagination,
      fileList,
      folderList,
      filters,
      currentFolderID,
      currentDetailsFolderID,
      isDetailsModalOpen,
    } = (yield select(getCompanyLibrary)) as CompanyLibraryState;

    // handle schedule modal case:
    if (clientContractOpts?.isScheduleModalOpen) {
      const folderIdForSchedule = clientContractOpts.scheduleFolderID || currentFolderID;
      if (!folderIdForSchedule) return;
      yield put(setState({ isLoadingList: true }));
      try {
        const params = {
          limit: -1,
          page: 1,
          current_folder_id: folderIdForSchedule,
          related_entity_type_list: ['schedule'],
          related_entity_id_list: [clientContractOpts.scheduleEntryID].filter(id => !!id),
        };
        const {
          data: { Data: FileData },
        } = yield call(api.get, `/v1/company-library/list`, { params });
        yield put(setFileList(FileData || [], false));
        yield put(setFolderList([], false));
      } finally {
        yield put(setState({ isLoadingList: false }));
      }
      return;
    }

    // handle sales pages case:
    if (clientContractOpts?.isClientPage || clientContractOpts?.isContractPage) {
      if (!currentFolderID && !isDetailsModalOpen) {
        return;
      }
      const { clientID, contractID, isClientPage, isContractPage } = clientContractOpts;
      yield put(setState({ activeSalesOpts: { clientID, contractID, isClientPage, isContractPage } }));
      yield put(
        setState(isDetailsModalOpen ? { detailsFolderListLoader: true } : { isLoadingList: true }),
      );
      try {
        const folderIdForQuery = isDetailsModalOpen
          ? currentDetailsFolderID ?? ''
          : currentFolderID ?? '';
        const res = yield call(
          api.get,
          `/v1/company-library/sales/list?client_id=${clientID ?? ''}&contract_id=${contractID ??
            ''}&folder_id=${folderIdForQuery}`,
        );

        if (res.status !== 200) {
          message.error('Failed to fetch documents. Please try reloading the page');
          return;
        }
        if (isDetailsModalOpen) {
          yield put(setDetailsFolderList(res.data.folders || []));
        } else {
          yield put(setFileList(res.data.files || [], false));
          yield put(setFolderList(res.data.folders || [], false));
        }
      } finally {
        yield put(
          setState(
            isDetailsModalOpen ? { detailsFolderListLoader: false } : { isLoadingList: false },
          ),
        );
      }
      return;
    }

    yield put(setState({ activeSalesOpts: null }));
    yield put(
      setState(isDetailsModalOpen ? { detailsFolderListLoader: true } : { isLoadingList: true }),
    );

    const queryParams = {
      limit: -1,
      page: 1,
    };
    let updatedFilters = filters;
    if (
      filters.relatedEntities.length > 0 &&
      filters.relatedEntityTypes.length > 0 &&
      filters.relatedEntities[0].Type !== filters.relatedEntityTypes[0]
    ) {
      updatedFilters.relatedEntities = [];
    }

    const folderId = isDetailsModalOpen ? currentDetailsFolderID : currentFolderID;

    const folderQueryParams = { ...queryParams, name: updatedFilters.fileName };

    queryParams['file_name'] = updatedFilters.fileName;
    queryParams['related_entity_type_list'] = updatedFilters.relatedEntityTypes;
    queryParams['related_entity_id_list'] = updatedFilters.relatedEntities.map(e => e.ID);
    if (!isDetailsModalOpen) {
      folderQueryParams['folder_name'] = updatedFilters.fileName;
    }
    // "Memos only" is filtered by the API (`is_memo`) across every folder, like a
    // name search, so the current folder is not sent and no folders are listed.
    const memosOnly = !isDetailsModalOpen && !!updatedFilters.memosOnly;
    if (memosOnly) {
      queryParams['is_memo'] = true;
    }

    if (!isInitialRender && folderId) {
      if (!memosOnly) {
        queryParams['current_folder_id'] = folderId;
      }
      folderQueryParams['current_folder_id'] = folderId;
    }

    let fileTotal = 0;
    if (!isDetailsModalOpen) {
      const {
        data: { Data: FileData, Total: FileTotal },
      } = yield call(api.get, `/v1/company-library/list`, {
        params: queryParams,
      });
      const loadMoreAvailable = FileTotal != 0 && fileList.length + FileData.length < FileTotal;
      if (queryParams.page === 1) {
        const updatedFileList = [...FileData];
        yield put(setFileList(updatedFileList, loadMoreAvailable));
      } else {
        const updatedFileList = [...fileList, ...FileData];
        yield put(setFileList(updatedFileList, loadMoreAvailable));
      }
      fileTotal = FileTotal;
    }

    const {
      data: { Data: FolderData, Total: FolderTotal },
    } = memosOnly
      ? { data: { Data: [], Total: 0 } }
      : yield call(api.get, `/v1/company-library/folders/list`, {
          params: folderQueryParams,
        });

    const folderLoadMoreAvailable =
      FolderTotal != 0 && folderList.length + FolderData.length < FolderTotal;
    if (queryParams.page === 1) {
      const updatedFolderList = [...FolderData];
      if (currentDetailsFolderID || isDetailsModalOpen) {
        yield put(setDetailsFolderList(updatedFolderList));
      }
      if (!currentDetailsFolderID && !isDetailsModalOpen) {
        yield put(setFolderList(updatedFolderList, folderLoadMoreAvailable));
      }
    } else {
      const updatedFolderList = [...folderList, ...FolderData];
      if (currentDetailsFolderID || isDetailsModalOpen) {
        yield put(setDetailsFolderList(updatedFolderList));
      }
      if (!currentDetailsFolderID && !isDetailsModalOpen) {
        yield put(setFolderList(updatedFolderList, folderLoadMoreAvailable));
      }
    }

    const newPagination: TablePaginationConfig = {
      total: fileTotal + FolderTotal,
      current: pagination.current,
      pageSize: pagination.pageSize,
    };
    yield updateRelations();
    yield put(
      setState({ pagination: newPagination, isLoadingList: false, detailsFolderListLoader: false }),
    );
  } catch (err) {
    console.log('Failed to load company library file list', err);
    yield put(setState({ isLoadingList: false, detailsFolderListLoader: false }));
  }
}
